/**
 * Convert any error from an API call into a user-friendly string for display in banners.
 *
 * Handles all 3 Dynasty Dugout backend error envelopes + axios + thrown Errors:
 *   1. Custom DynastyDugout exceptions: { success: false, error: { message: "..." } }
 *      (per backend/src/core/error_handlers.py dynasty_dugout_exception_handler)
 *   2. FastAPI HTTPException default: { detail: "..." | [...] }
 *      (used by dozens of plain HTTPException routes; default FastAPI envelope)
 *   3. Legacy soft-fail string: { error: "..." }
 *   4. Axios transport messages (Network Error etc.)
 *   5. Thrown Error instances (client-side validation, hook throws)
 *
 * Per 9o.6.d-followup divergence investigation: this helper replaces 6 inline broken
 * patterns that produced raw JSON blobs or generic "Request failed with status code X"
 * to users on plain HTTPException routes (most common backend error envelope).
 *
 * @param {unknown} err - The caught error (axios error, Error instance, or unknown shape)
 * @param {string} fallbackMessage - User-friendly default if no message could be extracted
 * @returns {string} Human-readable error string suitable for banner display
 */
export function formatApiError(err, fallbackMessage = 'Something went wrong') {
  // 1. Custom DynastyDugout exceptions (ValidationError, NotFoundError, etc.)
  const customMsg = err?.response?.data?.error?.message;
  if (typeof customMsg === 'string' && customMsg.length > 0) return customMsg;

  // 2. FastAPI HTTPException default: detail can be a string OR Pydantic-style array
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'string' && first.length > 0) return first;
    if (first && typeof first.msg === 'string' && first.msg.length > 0) return first.msg;
  }

  // 3. Legacy soft-fail string (e.g., older backends returning bare { error: "..." })
  const legacyError = err?.response?.data?.error;
  if (typeof legacyError === 'string' && legacyError.length > 0) return legacyError;

  // 4 & 5. Thrown Error.message OR axios message — skip generic axios noise that
  // hides the real backend message (we already tried .detail / .error.message above).
  const message = err?.message;
  if (typeof message === 'string' && message.length > 0 && !message.startsWith('Request failed with status code')) {
    return message;
  }

  return fallbackMessage;
}

export default formatApiError;
